import { KpiBar, formatKpiValue, theme } from '@arnold/common';
import styled from '@emotion/styled';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { QuestionSummary } from '../../../types';
import { metricDisplay, type CustomMetricSummary } from '@arnold/core';
import { formatMetricNumber } from '../../../definition/formatMetricNumber';
import { BandDistribution } from '../../../definition/BandDistribution';
import type { MetricCellPresentation } from '../types';
import type { MetricFormat } from '../../../definition/types';
import { CellStack, Chip, Chips, MetricLayout, Muted, SmallButton } from '../styles';

export const MissingValue = ({ reason = 'noAnswers' }: { reason?: string }) => {
  const { t } = useTranslation('newReport');
  return <Muted title={t(`table.${reason}`)}>—</Muted>;
};

export const MetricCell = ({
  summary,
  variant,
}: {
  summary?: QuestionSummary;
  variant: 'nps' | 'miniNps' | 'score';
}) => {
  const { t, i18n } = useTranslation('newReport');
  const score = variant === 'score' ? summary?.average : summary?.nps;
  if (!summary || score?.value === undefined) {
    return (
      <MissingValue
        reason={
          score?.status === 'unconfigured'
            ? 'configureScoring'
            : score?.status === 'invalid'
              ? 'invalidScoring'
              : 'noAnswers'
        }
      />
    );
  }
  const formattedValue =
    variant !== 'score'
      ? formatKpiValue(Math.round(score.value), 'nps')
      : formatMetricNumber(score.value, i18n.language);
  const buckets = [
    { label: t('table.detractors'), count: summary.nps.detractors, color: theme.colors.emotionDanger.default },
    { label: t('table.passives'), count: summary.nps.passives, color: theme.colors.emotionWarning.default },
    { label: t('table.promoters'), count: summary.nps.promoters, color: theme.colors.emotionSuccess.default },
  ];
  return (
    <CellStack>
      <MetricLayout>
        {variant === 'nps' ? (
          <Distribution title={buckets.map((bucket) => `${bucket.label}: ${bucket.count}`).join(' · ')}>
            {buckets.map((bucket, index) => (
              <Segment
                key={bucket.label}
                style={{ width: `${summary.nps.percentages[index]}%`, backgroundColor: bucket.color }}
              />
            ))}
          </Distribution>
        ) : (
          <div>
            {score.max > score.min && (
              <KpiBar
                value={score.value}
                min={score.min}
                max={score.max}
                variant={variant === 'miniNps' ? 'nps' : 'scale'}
                state={score.state}
              />
            )}
          </div>
        )}
        <strong>{formattedValue}</strong>
      </MetricLayout>
      {variant !== 'score' && <Muted>{t('table.npsBuckets', { min: summary.nps.inputMin })}</Muted>}
      <Muted>{t('table.validAnswers', { count: score.count })}</Muted>
    </CellStack>
  );
};

/** Binding and presentation are independent: one metric can occupy several differently rendered columns. */
export const CustomMetricCell = ({
  summary,
  presentation,
  format = 'number',
}: {
  summary?: CustomMetricSummary;
  presentation?: MetricCellPresentation;
  /** Display format of the metric definition; without it values are treated as plain numbers. */
  format?: MetricFormat;
}) => {
  const { t, i18n } = useTranslation('newReport');
  if (!summary || summary.value === undefined) return <MissingValue />;
  const percentFormat = format.endsWith('Percent');
  const variant =
    presentation ?? (summary.nps ? 'nps' : summary.bands ? 'distribution' : percentFormat ? 'percentage' : 'score');
  if (variant === 'distribution') {
    if (!summary.bands) return <MissingValue reason="invalidScoring" />;
    return (
      <CellStack>
        <MetricLayout>
          <BandDistribution bands={summary.bands} count={summary.count} compact />
          <strong>{`${formatMetricNumber(summary.value, i18n.language)}%`}</strong>
        </MetricLayout>
        <Muted>{t('definition.bandRespondents', { count: summary.count })}</Muted>
      </CellStack>
    );
  }
  const percentage = percentFormat
    ? metricDisplay(summary, format).value
    : summary.max > summary.min
      ? (100 * (summary.value - summary.min)) / (summary.max - summary.min)
      : undefined;
  const value = variant === 'percentage' ? percentage : summary.value;
  if (value === undefined || ((variant === 'nps' || variant === 'miniNps') && !summary.nps))
    return <MissingValue reason="invalidScoring" />;
  const buckets = summary.nps && [
    { name: 'detractors', count: summary.nps.detractors, color: theme.colors.emotionDanger.default },
    { name: 'passives', count: summary.nps.passives, color: theme.colors.emotionWarning.default },
    { name: 'promoters', count: summary.nps.promoters, color: theme.colors.emotionSuccess.default },
  ];
  return (
    <CellStack>
      <MetricLayout>
        {variant === 'nps' && buckets ? (
          <Distribution title={buckets.map((bucket) => `${t(`table.${bucket.name}`)}: ${bucket.count}`).join(' · ')}>
            {buckets.map((bucket) => (
              <Segment
                key={bucket.name}
                style={{ width: `${(100 * bucket.count) / summary.count}%`, backgroundColor: bucket.color }}
              />
            ))}
          </Distribution>
        ) : (
          variant !== 'number' && (
            <div>
              <KpiBar
                value={value}
                min={variant === 'percentage' ? 0 : summary.min}
                max={variant === 'percentage' ? 100 : summary.max}
                variant={variant === 'miniNps' ? 'nps' : 'scale'}
                state="normal"
              />
            </div>
          )
        )}
        <strong>
          {formatMetricNumber(
            value,
            i18n.language,
            variant === 'nps' || variant === 'miniNps' ? 'integer' : summary.scale ? 'scale' : undefined,
          )}
          {variant === 'percentage' ? '%' : ''}
        </strong>
      </MetricLayout>
      {(variant === 'nps' || variant === 'miniNps') && (
        <Muted>{t('table.npsBuckets', { min: summary.nps?.inputMin ?? 0 })}</Muted>
      )}
      {summary.nps && <Muted>{t('table.validAnswers', { count: summary.count })}</Muted>}
    </CellStack>
  );
};

export const ResponseRateCell = ({ value }: { value?: number }) => {
  const { i18n } = useTranslation('newReport');
  if (value === undefined) return <MissingValue />;
  return (
    <MetricLayout>
      <div>
        <KpiBar value={value} state="normal" />
      </div>
      <strong>
        {new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 0 }).format(value / 100)}
      </strong>
    </MetricLayout>
  );
};

export const ChoicesCell = ({ summary, individual }: { summary?: QuestionSummary; individual: boolean }) => {
  const { t, i18n } = useTranslation('newReport');
  const [expanded, setExpanded] = useState(false);
  if (!summary?.validCount || !summary.choices.length) return <MissingValue />;
  if (individual)
    return (
      <Chips>
        {summary.selectedLabels.map((label, index) => (
          <Chip key={index}>{label}</Chip>
        ))}
      </Chips>
    );
  return (
    <CellStack>
      {(expanded ? summary.choices : summary.choices.slice(0, 3)).map((choice) => (
        <Choice key={choice.index}>
          <ChoiceLabel>
            <span>{choice.label}</span>
            <Muted>
              {new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 0 }).format(choice.percentage)}%
            </Muted>
          </ChoiceLabel>
          <ChoiceTrack title={t('table.optionCount', { count: choice.count, total: summary.validCount })}>
            <Segment style={{ width: `${choice.percentage}%`, backgroundColor: theme.colors.brand.primary }} />
          </ChoiceTrack>
        </Choice>
      ))}
      {summary.choices.length > 3 && (
        <SmallButton type="button" onClick={() => setExpanded(!expanded)}>
          {expanded ? t('table.showLess') : t('table.moreOptions', { count: summary.choices.length - 3 })}
        </SmallButton>
      )}
      {summary.multipleChoice && <Muted>{t('table.multipleChoiceHint')}</Muted>}
    </CellStack>
  );
};

const Distribution = styled.div`
  display: flex;
  min-width: 0;
  height: 10px;
  overflow: hidden;
  border-radius: 4px;
  background: ${theme.colors.backgroundBasic.active};
`;
const Segment = styled.div`
  height: 100%;
  flex-shrink: 0;
`;
const Choice = styled.div`
  min-width: 0;
`;
const ChoiceLabel = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
`;
const ChoiceTrack = styled(Distribution)`
  height: 4px;
  margin-top: 4px;
`;
